import type { BookingQuestionType } from '../appointment-types';

export const BOOKING_TEXT_LINE_MODES = ['single_line', 'multi_line'] as const;

export type BookingTextLineMode = (typeof BOOKING_TEXT_LINE_MODES)[number];

const LINE_BREAKS = /\r\n|[\r\u2028\u2029]/g;

const NEWLINES = /\n/g;

const STRIPPED_CODE_POINT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x00, 0x08],
  [0x0b, 0x0c],
  [0x0e, 0x1f],
  [0x7f, 0x9f],
  [0x061c, 0x061c],
  [0x200b, 0x200b],
  [0x200e, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2060],
  [0x2066, 0x2069],
  // Array.from pairs valid surrogates into one code point, so only lone ones land here.
  [0xd800, 0xdfff],
  [0xfeff, 0xfeff],
];

/**
 * Strips controls (keeping tab and line breaks), bidi and zero-width characters
 * and lone surrogates (Postgres jsonb rejects them), but keeps ZWJ, ZWNJ and tag
 * characters: emoji sequences, flags and several scripts need them.
 */
export function sanitizeBookingText(
  text: string,
  lineMode: BookingTextLineMode = 'single_line',
): string {
  const visible = Array.from(text).filter(isKeptCharacter).join('');
  const unified = visible.replace(LINE_BREAKS, '\n').normalize('NFC');
  const lined =
    lineMode === 'single_line' ? unified.replace(NEWLINES, ' ') : unified;
  return lined.trim();
}

export function bookingTextLineModeFor(
  type: BookingQuestionType,
): BookingTextLineMode {
  return type === 'long_text' ? 'multi_line' : 'single_line';
}

export function countCodePoints(text: string): number {
  return Array.from(text).length;
}

/** Two options are duplicates when their keys are equal. */
export function bookingOptionDuplicateKey(option: string): string {
  return sanitizeBookingText(option).toLowerCase().normalize('NFC');
}

function isKeptCharacter(character: string): boolean {
  const codePoint = character.codePointAt(0) ?? 0;
  return !STRIPPED_CODE_POINT_RANGES.some(
    ([first, last]) => codePoint >= first && codePoint <= last,
  );
}
